/**
 * Sequential async poller used by the PWA for network-backed UI state.
 *
 * Invariants:
 * - never overlaps requests within one generation
 * - schedules the next request only after the previous one settles
 * - aborts active work on stop/hidden generation changes
 * - stale generations never publish success/error callbacks
 */
export function createSequentialPoller({
  request,
  onSuccess,
  onError,
  intervalMs,
  initialDelayMs = 0,
  pauseWhenHidden = true,
  errorBackoffMultiplier = 1,
  maxErrorIntervalMs = null,
  windowTarget = globalThis.window,
  documentTarget = globalThis.document,
}) {
  if (typeof request !== "function") throw new TypeError("request muss eine Funktion sein.");
  const delay = Math.max(0, Number(intervalMs) || 0);
  const initialDelay = Math.max(0, Number(initialDelayMs) || 0);
  const backoffMultiplier = Math.max(1, Number(errorBackoffMultiplier) || 1);
  const maxBackoffDelay = maxErrorIntervalMs == null
    ? delay
    : Math.max(delay, Number(maxErrorIntervalMs) || delay);
  let active = false;
  let running = false;
  let timer = null;
  let controller = null;
  let generation = 0;
  let immediatePending = false;
  let consecutiveErrors = 0;

  const clearTimer = () => {
    if (timer !== null) {
      windowTarget.clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = (waitMs) => {
    if (!active) return;
    clearTimer();
    timer = windowTarget.setTimeout(() => {
      timer = null;
      void run();
    }, Math.max(0, waitMs));
  };

  const isVisible = () => !pauseWhenHidden || !documentTarget || documentTarget.visibilityState !== "hidden";

  const run = async () => {
    if (!active) return;
    if (running) {
      immediatePending = true;
      return;
    }
    if (!isVisible()) return;

    running = true;
    immediatePending = false;
    const runGeneration = generation;
    const currentController = new AbortController();
    controller = currentController;
    const context = {
      signal: currentController.signal,
      generation: runGeneration,
      isCurrent: () => active && generation === runGeneration && !currentController.signal.aborted,
    };

    try {
      const value = await request(context);
      if (context.isCurrent()) {
        consecutiveErrors = 0;
        if (typeof onSuccess === "function") onSuccess(value, context);
      }
    } catch (error) {
      if (context.isCurrent()) {
        consecutiveErrors += 1;
        if (typeof onError === "function") onError(error, context);
      }
    } finally {
      if (controller === currentController) controller = null;
      running = false;
      if (!active) return;
      if (generation !== runGeneration) {
        if (immediatePending && isVisible()) {
          immediatePending = false;
          schedule(0);
        }
        return;
      }
      const retryDelay = consecutiveErrors > 0 && backoffMultiplier > 1
        ? Math.min(maxBackoffDelay, delay * (backoffMultiplier ** Math.max(0, consecutiveErrors - 1)))
        : delay;
      schedule(immediatePending ? 0 : retryDelay);
    }
  };

  const trigger = () => {
    if (!active) return;
    if (running) {
      immediatePending = true;
      return;
    }
    if (isVisible()) schedule(0);
  };

  const onVisibilityChange = () => {
    if (!active) return;
    generation += 1;
    controller?.abort();
    controller = null;
    clearTimer();
    if (isVisible()) {
      if (running) immediatePending = true;
      else schedule(0);
    } else {
      immediatePending = false;
    }
  };

  const start = () => {
    if (active) return;
    active = true;
    generation += 1;
    documentTarget?.addEventListener?.("visibilitychange", onVisibilityChange);
    if (isVisible()) schedule(initialDelay);
  };

  const stop = () => {
    if (!active) return;
    active = false;
    generation += 1;
    immediatePending = false;
    clearTimer();
    controller?.abort();
    controller = null;
    documentTarget?.removeEventListener?.("visibilitychange", onVisibilityChange);
  };

  return {
    start,
    stop,
    trigger,
    getState: () => ({ active, running, pending: immediatePending, generation, scheduled: timer !== null, consecutiveErrors }),
  };
}
